/**
 * The provider's dashboard: a bank, a finance company, a fintech.
 *
 * The queue first, because work waiting is the only thing on this page that
 * costs somebody money today. Then this provider's own conversion, then its
 * products and the share of routable flow it has taken.
 *
 * One figure is shown comparatively and only one: the offer rate against the
 * platform median. It is shown only when the provider's own volume is large
 * enough for the comparison to mean anything, and the server decides that, not
 * this screen.
 */
import { Link } from 'react-router-dom'

import type { Dict, Grant, SectionMeta } from '../../api/types'
import { useSession } from '../../app/session'
import { appPath } from '../../app/paths'
import { Badge, Empty, Note, toneOf } from '../../components/bits'
import { PageHead } from '../../components/PageHead'
import { Panel, Cols } from '../../components/Panel'
import { Bars, StateChips, TileStrip } from './common'
import type { TileData } from './common'

export function ProviderDashboard({ data, section, grant }: {
  data: Dict; section: SectionMeta; grant?: Grant
}) {
  const { n, amount, d, lbl, boot } = useSession()
  const queue = (data.queue || {}) as Dict
  const mine = (data.mine || {}) as Dict
  const revenue = (data.revenue || {}) as Dict
  const products = (data.products || []) as Dict[]
  const byStatus = (data.by_status || []) as Dict[]
  const shariaDue = (data.sharia_due || []) as Dict[]
  const flow = data.flow as Dict | null
  const comparable = Boolean(data.comparable)

  return (
    <>
      <PageHead section={section} grant={grant} />
      <TileStrip tiles={(data.tiles || []) as TileData[]} />

      <Cols>
        <Panel title="Your queue" explain="application.queue">
          <Bars rows={[
            { k: 'open now', n: queue.open },
            { k: 'due within 24 hours', n: queue.due_24h },
            { k: 'past the service level', n: queue.breached },
            { k: 'waiting on the customer', n: queue.awaiting_customer },
          ]} labelKey="k" valueKey="n" />
          {Number(queue.breached) > 0 && (
            <Note kind="bad">
              <b>{n(queue.breached)} applications are past the service level</b>{' '}
              on your workflow binding. The clock runs from when the application
              reached you, and time the customer spends answering a question does
              not count against you.
            </Note>
          )}
          {byStatus.length > 0 && (
            <>
              <h4>By state</h4>
              <StateChips rows={byStatus} labelKey="status" valueKey="n" />
            </>
          )}
          <Link className="btn sm" to={appPath('/app/applications')}>Open the queue</Link>
        </Panel>

        <Panel title="What you have decided" explain="application.decisions">
          <Bars rows={[
            { k: 'decided', n: mine.decided },
            { k: 'offered', n: mine.offered },
            { k: 'accepted', n: mine.accepted },
            { k: 'disbursed', n: mine.disbursed },
          ]} labelKey="k" valueKey="n" />
          <p className="xs muted">
            Median time to decide {mine.median_hours === null
              ? 'is not yet measurable' : `${n(mine.median_hours, 1)} hours`}.
          </p>
          {comparable && flow !== null ? (
            <Note kind="info">
              <b>Your offer rate is {n(data.offer_rate, 1)}%</b> against a
              platform median of {n(data.platform_offer_rate, 1)}%. Shown because
              your volume is large enough for the comparison to carry meaning.
            </Note>
          ) : (
            <p className="xs muted">
              An offer rate against the platform is not shown: your volume in the
              period is below the floor where the comparison would say anything
              other than noise.
            </p>
          )}
        </Panel>
      </Cols>

      <Panel title={`Your products · ${products.length}`} explain="product.catalogue" tight>
        {products.length === 0 ? (
          <Empty title="No product is published">
            Nothing can be routed to you until a product is published with a rule
            set attached.
          </Empty>
        ) : (
          <div className="tbl-wrap">
            <table className="dl">
              <thead>
                <tr>
                  <th>Product</th><th>Compliance</th><th>Structure</th>
                  <th className="num">Minimum</th><th className="num">Maximum</th>
                  <th className="num">Monthly capacity</th>
                  <th className="num">Routed this month</th>
                  <th>Rule set</th><th>State</th><th className="acts" />
                </tr>
              </thead>
              <tbody>
                {products.map((p) => {
                  const capacity = Number(p.monthly_capacity) || 0
                  const used = Number(p.routed_this_month) || 0
                  const tight = capacity > 0 && used / capacity > 0.8
                  return (
                    <tr key={String(p.product_id)}>
                      <td data-label="Product">{p.name_en as string}</td>
                      <td data-label="Compliance">
                        <Badge tone={toneOf(p.compliance_type)}>
                          {lbl(p.compliance_type)}
                        </Badge>
                      </td>
                      <td data-label="Structure">
                        {p.sharia_structure
                          ? <Badge tone="muted">{lbl(p.sharia_structure)}</Badge>
                          : <span className="muted">—</span>}
                      </td>
                      <td data-label="Minimum" className="num">{amount(p.min_amount)}</td>
                      <td data-label="Maximum" className="num">{amount(p.max_amount)}</td>
                      <td data-label="Monthly capacity" className="num">
                        {capacity ? n(capacity) : <span className="muted">unset</span>}
                      </td>
                      <td data-label="Routed this month" className="num">
                        {tight ? <Badge tone="warn">{n(used)}</Badge> : n(used)}
                      </td>
                      <td data-label="Rule set">
                        <span className="mono clip">
                          {p.ruleset_id ? `${p.ruleset_id} v${p.ruleset_version}` : '—'}
                        </span>
                      </td>
                      <td data-label="State">
                        <Badge tone={toneOf(p.status)}>{lbl(p.status)}</Badge>
                      </td>
                      <td className="acts">
                        <Link className="btn sm"
                              to={appPath(`/app/products/${p.product_id}`)}>Open</Link>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="xs muted">
          Capacity is a filter on routing, not a target. A product at capacity
          stops receiving applications rather than receiving them and declining
          them, because a decline recorded against a provider that had no room is
          a decline the applicant is told nothing useful by.
        </p>
      </Panel>

      <Cols>
        <Panel title="Settled business" explain="billing.revenue">
          <div className="big-value">
            <b className="num">{amount(revenue.volume, null, 0)}</b>
            <span className="muted">disbursed</span>
          </div>
          <p className="xs muted">
            {n(revenue.disbursements)} disbursements, platform fees{' '}
            {amount(revenue.fees, null, 0)}. Fees are computed from the published
            schedule, and fee level plays no part in which provider an
            application reaches: the routing weights are approval fit{' '}
            {n(boot.constants.weights.approval_fit * 100)}%, speed{' '}
            {n(boot.constants.weights.speed * 100)}%, product fit{' '}
            {n(boot.constants.weights.product_fit * 100)}% and stated preference{' '}
            {n(boot.constants.weights.preference * 100)}%.
          </p>
        </Panel>

        {shariaDue.length > 0 && (
          <Panel title="Awaiting the Sharia board" explain="product.sharia_review" tight>
            <div className="tbl-wrap">
              <table className="dl">
                <thead><tr><th>Product</th><th>Structure</th><th>Submitted</th></tr></thead>
                <tbody>
                  {shariaDue.map((s, i) => (
                    <tr key={i}>
                      <td data-label="Product">{s.name_en as string}</td>
                      <td data-label="Structure">{lbl(s.sharia_structure)}</td>
                      <td data-label="Submitted">
                        {s.submitted_at ? d(s.submitted_at) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="xs muted">
              A Sharia compliant product cannot be published while its review is
              outstanding. That is a constraint in the schema rather than a step
              in a checklist.
            </p>
          </Panel>
        )}
      </Cols>
    </>
  )
}
